import React, { useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";

interface TimeSlot {
  id: string;
  start_time: string;
  end_time: string;
}

interface ShiftModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (timeSlots: TimeSlot[]) => void;
  employeeName: string;
  date: Date;
  existingShifts?: TimeSlot[];
}

const ShiftModal: React.FC<ShiftModalProps> = ({
  isOpen,
  onClose,
  onSave,
  employeeName,
  date,
  existingShifts = [],
}) => {
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>(
    existingShifts.length > 0
      ? existingShifts
      : [{ id: Date.now().toString(), start_time: "", end_time: "" }]
  );
  const [loading, setLoading] = useState(false);

  // Purple theme colors
  const primaryPurple = "#885ABB";
  const darkPurple = "#402B69";
  const lightPurple = "#C4A6E1";
  const veryLightPurple = "#F1ECFB";

  // Format date for display
  const formatDateDisplay = (date: Date): string => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`;
  };

  // Handle add new time slot
  const handleAddTimeSlot = () => {
    setTimeSlots([
      ...timeSlots,
      { id: Date.now().toString(), start_time: "", end_time: "" },
    ]);
  };

  // Handle remove time slot
  const handleRemoveTimeSlot = (id: string) => {
    if (timeSlots.length > 1) {
      setTimeSlots(timeSlots.filter((slot) => slot.id !== id));
    }
  };

  // Handle time slot change
  const handleTimeSlotChange = (
    id: string,
    field: "start_time" | "end_time",
    value: string
  ) => {
    setTimeSlots(
      timeSlots.map((slot) =>
        slot.id === id ? { ...slot, [field]: value } : slot
      )
    );
  };

  // Handle save
  const handleSave = async () => {
    // Validate time slots
    const validSlots = timeSlots.filter(
      (slot) => slot.start_time && slot.end_time
    );

    if (validSlots.length === 0) {
      alert("Please add at least one valid time slot");
      return;
    }

    // Validate time ranges
    for (const slot of validSlots) {
      if (slot.start_time >= slot.end_time) {
        alert("End time must be after start time");
        return;
      }
    }

    setLoading(true);
    try {
      await onSave(validSlots);
      onClose();
    } catch (error) {
      console.error("Error saving shifts:", error);
    } finally {
      setLoading(false);
    }
  };

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      // Initialize with existing shifts or empty slot
      if (existingShifts.length > 0) {
        setTimeSlots(existingShifts);
      } else {
        setTimeSlots([{ id: Date.now().toString(), start_time: "", end_time: "" }]);
      }
    }
  }, [isOpen, existingShifts]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-md mx-4 border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: lightPurple }}>
          <h2 className="text-lg font-semibold" style={{ color: darkPurple }}>
            {employeeName}'s {formatDateDisplay(date)}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" style={{ color: primaryPurple }} strokeWidth={2.5} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {timeSlots.map((slot, index) => (
            <div key={slot.id} className="space-y-3">
              <div className="flex items-end gap-3">
                {/* Start Time */}
                <div className="flex-1">
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: darkPurple }}
                  >
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={slot.start_time}
                    onChange={(e) =>
                      handleTimeSlotChange(slot.id, "start_time", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-sm"
                    style={{
                      borderColor: lightPurple,
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = primaryPurple;
                      e.target.style.boxShadow = `0 0 0 2px ${veryLightPurple}`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = lightPurple;
                      e.target.style.boxShadow = 'none';
                    }}
                    placeholder="Start Time"
                  />
                </div>

                {/* End Time */}
                <div className="flex-1">
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: darkPurple }}
                  >
                    End Time
                  </label>
                  <input
                    type="time"
                    value={slot.end_time}
                    onChange={(e) =>
                      handleTimeSlotChange(slot.id, "end_time", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-sm"
                    style={{
                      borderColor: lightPurple,
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = primaryPurple;
                      e.target.style.boxShadow = `0 0 0 2px ${veryLightPurple}`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = lightPurple;
                      e.target.style.boxShadow = 'none';
                    }}
                    placeholder="End Time"
                  />
                </div>

                {/* Add/Remove Buttons */}
                <div className="flex gap-2">
                  {timeSlots.length > 1 && (
                    <button
                      onClick={() => handleRemoveTimeSlot(slot.id)}
                      className="p-2 rounded-md border hover:bg-red-50 transition-colors"
                      style={{ borderColor: lightPurple }}
                      aria-label="Remove time slot"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  )}
                  {index === timeSlots.length - 1 && (
                    <button
                      onClick={handleAddTimeSlot}
                      className="p-2 rounded-md border hover:opacity-90 transition-colors"
                      style={{
                        borderColor: primaryPurple,
                        backgroundColor: veryLightPurple,
                      }}
                      aria-label="Add time slot"
                    >
                      <Plus className="w-4 h-4" style={{ color: primaryPurple }} strokeWidth={3} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50" style={{ borderColor: lightPurple }}>
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-md text-white font-medium transition-colors disabled:opacity-50"
            style={{ backgroundColor: "#EF4444" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 rounded-md text-white font-medium transition-colors disabled:opacity-50 hover:opacity-90"
            style={{ backgroundColor: primaryPurple }}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShiftModal;
